/**
 * XLM - Innovation & Technology | João Rodrigues | v2.0 | 2026*
 * 
 * Editor de Imagem (OCR/Tesseract):
 * - Memory Leak: Tesseract Worker terminado via useEffect cleanup
 * - Performance: renderização reativa (eliminado loop 60fps constante)
 * - Performance: getBoundingClientRect removido do onMouseMove (evita forced reflow)
 * - UI Blocking: normalização processada async sem bloquear main thread
 */

import { useEffect, useRef, useState } from "react";
import { UserFile } from "../../types/UserFile";
import BootstrapModal from "../../util/BootstrapModal";
import { Bicon, Button } from "../../util/BootstrapIcons";
import { AnonimizeImage } from "../../types/AnonimizeImage";
import { normalizeEntityString } from "../../types/Entity";


export function ImageEditorModal(props: { file: UserFile }) {
    const workerRef = useRef<any>(null);

    const [isReady,setIsReady] = useState(false);
    useEffect(() => {
        let isAlive = true; 
        const Tesseract = (window as any).Tesseract;

        const init = async() => {
            try {
                const worker = await Tesseract.createWorker('por', 1, {
                    workerPath: './assets/tesseract-worker.min.js',
                    corePath: './assets/tesseract-core-simd.wasm',
                });
                if(!isAlive){
                    await worker.terminate();
                    return;
                }
                await worker.loadLanguage("por");
                await worker.initialize("por");
                if (isAlive) {
                    workerRef.current = worker;
                    setIsReady(true);
                }
            } catch (e) {
                if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
                    console.error("Tesseract: falha ao inicializar worker", e);
                }
            }
        };
        
        init();

        return () => {
            isAlive = false;
            if (workerRef.current) {
                workerRef.current.terminate();
            }
        };
    }, []);


    const [imageElmt, setImageElmt] = useState<HTMLImageElement>()

    const onShow = (elmt: HTMLElement | null) => {
        if (elmt?.tagName === "IMG") {
            setImageElmt(elmt as HTMLImageElement);
        }
        else {
            setImageElmt(undefined);
        }
    }

    const onHide = () => setImageElmt(undefined)

    return <BootstrapModal id="modal-image-editor" onShow={onShow} onHide={onHide}>
    <div className="modal-body">
        <div className="modal-header">
            <div><h5 className="modal-title" id="modal-types-label">Anonimizar Imagem</h5></div>
        </div>
        <div className="modal-body">
            {!isReady ? 
                <div className="text-center py-3">A carregar motor de OCR...</div>
                :
                (imageElmt ?
                    <ImageEditor file={props.file} imageElmt={imageElmt} worker={workerRef.current} />
                    :
                    <>Selecione uma imagem para a editar</>)
            }
        </div>
    </div>
</BootstrapModal>



}

function ImageEditor(props: { file: UserFile, imageElmt: HTMLImageElement, worker: any }) {
    let canvasBackgroundRef = useRef<HTMLCanvasElement>(null)
    let canvasForegroundRef = useRef<HTMLCanvasElement>(null)
    let ctxBackgroundRef = useRef<CanvasRenderingContext2D | null>(null)
    let ctxForegroundRef = useRef<CanvasRenderingContext2D | null>(null)
    let originalImage = useRef<HTMLImageElement | null>(null)
    let animFrameRequest = useRef<number>(0)
    let boxStart = useRef<number[] | null>(null)
    let mousePos = useRef<number[] | null>(null)
    let boxes = useRef<[number, number, number, number][]>([])
    let colorInputRef = useRef<HTMLInputElement>(null)
    let checkRemoveInput = useRef<HTMLInputElement>(null)
    let scaleRef = useRef<number>(1);

    const [isUpdating, setIsUpdating] = useState(false);
    const requestUpdate = () => {
        if (animFrameRequest.current !== 0) return; 
        
        animFrameRequest.current = requestAnimationFrame(() => {
            const color = colorInputRef.current?.value || "#000000";
            if (ctxBackgroundRef.current && ctxForegroundRef.current && originalImage.current) {
                draw(
                    ctxBackgroundRef.current, 
                    ctxForegroundRef.current, 
                    originalImage.current, 
                    boxStart.current, 
                    mousePos.current, 
                    boxes.current, 
                    color,
                    scaleRef.current

                );
            }
            animFrameRequest.current = 0;
        });
    };

    useEffect(() => {
        if (!canvasBackgroundRef.current) return;
        if (!canvasForegroundRef.current) return;
        if (!colorInputRef.current) return;

        ctxBackgroundRef.current = canvasBackgroundRef.current.getContext("2d");
        ctxForegroundRef.current = canvasForegroundRef.current.getContext("2d");

        if (!ctxBackgroundRef.current) return;
        if (!ctxForegroundRef.current) return;

        const imgData = props.file.images[parseInt(props.imageElmt.dataset.imageId!)];
        originalImage.current = setup(ctxBackgroundRef.current, ctxForegroundRef.current, imgData)
        originalImage.current.onload = () => {
            const img = originalImage.current!;
            const c = img.height > 800 ? 800 / img.height : 1;
            scaleRef.current = c;
            
            ctxBackgroundRef.current!.canvas.width = img.width * c;
            ctxBackgroundRef.current!.canvas.height = img.height * c;
            ctxForegroundRef.current!.canvas.width = img.width * c;
            ctxForegroundRef.current!.canvas.height = img.height * c;
            requestUpdate();
        };
        boxes.current = props.file.images[parseInt(props.imageElmt.dataset.imageId!)].boxes
        colorInputRef.current.value = props.file.images[parseInt(props.imageElmt.dataset.imageId!)].boxColor || "#00000"

        requestUpdate();

        return () => {
            if(animFrameRequest.current !== 0){
                cancelAnimationFrame(animFrameRequest.current!)
            }
        };
    }, [props.imageElmt, props.file.images])

    const onMouseDown = (evt: React.MouseEvent) => {
        if (!canvasForegroundRef.current) return;
    
        const { offsetX, offsetY } = evt.nativeEvent;
        boxStart.current = [offsetX, offsetY];
        mousePos.current = [offsetX, offsetY];
        requestUpdate();
    }

    const onMouseMove = (evt: React.MouseEvent) => {
        if (!boxStart.current) return; // Só atualiza se estiver a desenhar
        const { offsetX, offsetY } = evt.nativeEvent;
        mousePos.current = [offsetX, offsetY];
        requestUpdate();

    }

    const intersects: (r1: number[], r2: number[]) => boolean = (
        [r1TopLeftX, r1TopLeftY, r1BottomRightX, r1BottomRightY],
        [r2TopLeftX, r2TopLeftY, r2BottomRightX, r2BottomRightY],
    ) => (r1TopLeftX < r2BottomRightX) && (r1BottomRightX > r2TopLeftX) && (r1TopLeftY < r2BottomRightY) && (r1BottomRightY > r2TopLeftY)

    const onMouseUp = (evt: React.MouseEvent) => {
        const { offsetX, offsetY } = evt.nativeEvent;
        mousePos.current = [offsetX, offsetY];

        if (!boxStart.current) return;
        
        const [initialX, initialY] = boxStart.current;
        if (checkRemoveInput.current?.checked) {
            const selectionRect = [
                Math.min(offsetX, initialX),
                Math.min(offsetY, initialY),
                Math.max(offsetX, initialX),
                Math.max(offsetY, initialY)
            ];

            boxes.current = boxes.current.filter(([x0, y0, x1, y1]) => {
                const targetRect = [
                    Math.min(x0, x1), 
                    Math.min(y0, y1), 
                    Math.max(x0, x1), 
                    Math.max(y0, y1)
                ];
                
                return !intersects(selectionRect, targetRect);
            });
        } else {
            boxes.current.push([initialX, initialY, offsetX, offsetY]);
        }
        boxStart.current = null;
        requestUpdate();

  
    }

    const onClickSave = async () => {
        try {
            if (boxes.current.length === 0) {
                props.file.images[parseInt(props.imageElmt.dataset.imageId!)].anonimizedSrc = undefined
            }
            else {
                props.file.images[parseInt(props.imageElmt.dataset.imageId!)].anonimizedSrc = drawFinal(originalImage.current!, boxes.current, colorInputRef.current?.value || "#000000")
            }
            props.file.images[parseInt(props.imageElmt.dataset.imageId!)].boxes = boxes.current
            props.file.images[parseInt(props.imageElmt.dataset.imageId!)].boxColor = colorInputRef.current?.value || "#000000"
            props.file.notifyImages()
            await props.file.save()
         } catch (e) {
            if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
                console.error("Imagem: falha ao guardar", e);
            }
        }
    }

    const onClickRestore = async () => {
        try {
            boxes.current = []
            props.file.images[parseInt(props.imageElmt.dataset.imageId!)].anonimizedSrc = undefined
            props.file.images[parseInt(props.imageElmt.dataset.imageId!)].boxes = []
            props.file.images[parseInt(props.imageElmt.dataset.imageId!)].boxColor = colorInputRef.current?.value || "#000000"
            props.file.notifyImages()
            await props.file.save()
        } catch (e) {
            if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
                console.error("Imagem: falha ao restaurar", e);
            }
        }
    }

    const onClickRecognize = async () => {
        setIsUpdating(true);
        try{
            const { data: { words } } = await props.worker.recognize(canvasBackgroundRef.current!)

            const normalizedPool = props.file.pool.entities.flatMap(e => 
                e.offsets.map(o => normalizeEntityString(o.preview))
            );
            
            boxes.current = words.filter(({text}:any) => {
                if(text.length <= 3) return false;
                const normText = normalizeEntityString(text);
                return normalizedPool.length === 0 || normalizedPool.some(p => p.includes(normText));
            })
            .map(({ bbox }: any) => [bbox.x0, bbox.y0, bbox.x1, bbox.y1]);
        
            requestUpdate();
        } catch (e) {
            if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
                console.error("Tesseract: falha no reconhecimento OCR", e);
            }
        } 
        finally {
            setIsUpdating(false);
        }
    }


    return <>
        <div className="d-flex align-items-center justify-content-center">
            <Button className="btn btn-primary" i="search" text="Encontrar texto" onClick={onClickRecognize} disabled={isUpdating} />
            <span className="mx-1"><Bicon n="dot" /></span>
            <input type="radio" className="btn-check" name="image-mode" id="add-box-button" autoComplete="off" defaultChecked={true} />
            <label className="btn btn-outline-success" htmlFor="add-box-button"><Bicon n="plus-square-dotted" /> Adicionar</label>
            <input ref={checkRemoveInput} type="radio" className="btn-check" name="image-mode" id="remove-box-button" autoComplete="off" defaultChecked={false} />
            <label className="btn btn-outline-danger" htmlFor="remove-box-button"><Bicon n="dash-square" /> Remover</label>
            <span className="mx-1"><Bicon n="dot" /></span>
            <input type="color" ref={colorInputRef} className="form-control form-control-color" title="Escolher cor" />
        </div>
        <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", height: "800px", margin: "10px" }}>
            <canvas ref={canvasBackgroundRef} style={{ position: "absolute", border: "2px solid var(--secondary-gold)" }} />
            <canvas ref={canvasForegroundRef} style={{ position: "absolute", border: "2px solid var(--secondary-gold)" }} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} />
        </div>
        <div className="d-flex">
            <div className="flex-grow-1"></div>
            <Button className="btn btn-danger mx-1" i="arrow-counterclockwise" text="Recomeçar" onClick={onClickRestore} />
            <Button className="btn btn-success mx-1" i="save" text="Guardar e fechar" data-bs-dismiss="modal" onClick={onClickSave} />
        </div>

    </>
}

function setup(backCtx: CanvasRenderingContext2D, foreCtx: CanvasRenderingContext2D, image: AnonimizeImage) {
    let originalImage = new Image();
    originalImage.src = image.originalSrc;
    let anonimizedImage = null;
    if (image.anonimizedSrc) {
        anonimizedImage = new Image();
        anonimizedImage.src = image.anonimizedSrc;
    }
    originalImage.onload = () => {
        let c = 1;
        if (originalImage.height > 800) {
            c = 800 / originalImage.height
        }
        backCtx.canvas.width = originalImage.width * c
        backCtx.canvas.height = originalImage.height * c
        foreCtx.canvas.width = originalImage.width * c
        foreCtx.canvas.height = originalImage.height * c
    }
    return originalImage
}

function drawFinal(image: HTMLImageElement, boxes: [number, number, number, number][], color: string) {
    let { width, height } = image;
    let canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    let c = 1;
    if (height > 800) {
        c = 800 / height
    }
    let inv_c = 1 / c;
    let ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    ctx.drawImage(image, 0, 0, width, height);
    ctx.fillStyle = color;
    for (let box of boxes) {
        ctx.beginPath();
        ctx.moveTo(box[0] * inv_c, box[1] * inv_c)
        ctx.lineTo(box[0] * inv_c, box[3] * inv_c)
        ctx.lineTo(box[2] * inv_c, box[3] * inv_c)
        ctx.lineTo(box[2] * inv_c, box[1] * inv_c)
        ctx.fill();
    }

    return canvas.toDataURL();

}


function draw(
    backCtx: CanvasRenderingContext2D, 
    foreCtx: CanvasRenderingContext2D, 
    originalImage: HTMLImageElement, 
    boxStart: number[] | null, 
    mousePos: number[] | null, 
    boxes: [number, number, number, number][], 
    color: string,
    scale: number
) {
    const w = backCtx.canvas.width;
    const h = backCtx.canvas.height;

    backCtx.clearRect(0, 0, w, h);
    foreCtx.clearRect(0, 0, w, h);

    backCtx.drawImage(originalImage, 0, 0, originalImage.width * scale, originalImage.height * scale);

    backCtx.fillStyle = `${color}aa`;
    backCtx.strokeStyle = "#00000044";
    backCtx.lineWidth = 1;

    for (const [x0, y0, x1, y1] of boxes) {
        const x = Math.min(x0, x1);
        const y = Math.min(y0, y1);
        const bw = Math.abs(x1 - x0);
        const bh = Math.abs(y1 - y0);
        
        backCtx.fillRect(x, y, bw, bh);
        backCtx.strokeRect(x, y, bw, bh);
    }

    if (boxStart && mousePos) {
        foreCtx.strokeStyle = "#000000aa";
        foreCtx.fillStyle = `${color}66`;
        
        const [startX, startY] = boxStart;
        const [currX, currY] = mousePos;

        foreCtx.beginPath();
        foreCtx.rect(startX, startY, currX - startX, currY - startY);
        foreCtx.fill();
        foreCtx.stroke();
    }
}
